import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import ModalAtom from "../../../components/atoms/modal/modal";
import { FilterBar } from "../../../components/molecule/molecule";
import ProjectHeader from "../../../components/ProjectHeader";
import {
	clearBoQState,
	clearBoQValidation,
	fetchBoQEditorData,
	selectBoQGaebData,
	selectBoQHasInvoice,
	selectBoQHasUnsavedChanges,
	selectBoQLoading,
	selectBoQLv,
	selectBoQStatus,
	selectBoQValidationResult,
	setHasUnsavedChanges,
	setReopenResult,
	setSaveResult,
	setValidationResult,
} from "../../../store/slices/boqSlice";
import { selectProjectDetails } from "../../../store/slices/projectDetailsSlice";
import { showToast } from "../../../utils/method";
import "./boq-editor.scss";
import BoQEmptyState from "./components/BoQEmptyState";
import BoQGrid, { getDefaultBoQRow } from "./components/BoQGrid";
import BoQToolbar from "./components/BoQToolbar";
import BoQSchemaModal from "./components/BoQSchemaModal";
import BoQEditorTour, { TOUR_STORAGE_KEY } from "./components/BoQEditorTour";
import { BOQ_STATUS } from "../../../constants/boq-status";
import { ELEMENT_TYPES } from "./constants/boqElementTypes";
import { avaToEditorNodes, editorNodesToAva, DEFAULT_GAEB_ITEM_NUMBER_SCHEMA } from "./utils/boqAvaAdapter";
import {
	addNodeAfterSibling,
	addNodeToTree,
	cloneNodeWithNewIds,
	countRootNodes,
	findAncestorIds,
	findNodeInTree,
	isDescendantOf,
	moveNodeInTree,
	removeNodeFromTree,
	updateNodeInTree,
} from "./utils/boqTreeUtils";
import {
	cascadeItemNumberChange,
	generateItemNumbers,
	resequenceSiblingsAfter,
	validateItemNumbers,
} from "./utils/boqItemNumbers";
import {
	useReopenBoQMutation,
	useSaveBoQMutation,
	useValidateBoQMutation,
} from "../../../store/services/boqService";
import { DEFAULT_VAT_PERCENTAGE } from "../../../constants/vat-config";

/** Maps AVA severity to a small colored circle icon (matches design mockup). */
const SEVERITY_STYLE = {
	Information: { bg: "#fff3cd", color: "#d97706", label: "i" },
	Warning: { bg: "#fff3cd", color: "#d97706", label: "!" },
	Error: { bg: "#fee2e2", color: "#dc2626", label: "×" },
	Critical: { bg: "#fee2e2", color: "#dc2626", label: "×" },
};

const SeverityIcon = ({ severity }) => {
	const isError = severity === "Error" || severity === "Critical";
	return (
		<span
			className={`boq-severity-icon boq-severity-icon--${isError ? "error" : "info"}`}
			aria-hidden
		>
			{(SEVERITY_STYLE[severity] ?? SEVERITY_STYLE.Information).label}
		</span>
	);
};

/**
 * Validation result body - used in both the badge-detail modal and the V&F modal.
 *
 * Groups duplicate messages by (severity + text), shows a count badge per group,
 * and - when `onElementClick` is provided - makes each row a button that navigates
 * directly to the offending element and opens it in edit mode.
 *
 * IDs match because editorNodeToAvaElement passes node.id to AVA Cloud as element.id,
 * and avaToEditorNodes reads element.id back as node.id. No extra mapping needed.
 */
const ValidationResultBody = ({ result, t, onElementClick }) => {
	if (!result) return null;

	if (result.isSchemaCompliant) {
		return (
			<div className="alert alert-success mb-0">
				<strong>{t("boq.validate.valid")}</strong>
			</div>
		);
	}

	// De-duplicate check results: group by (severity + message), collecting elementIds.
	const grouped = [];
	const seen = new Map();
	for (const cr of result.checkResults ?? []) {
		const key = `${cr.severity}||${cr.message}`;
		const elementId = cr.projectValidationCheckDetails?.elementId ?? null;
		if (seen.has(key)) {
			const g = grouped[seen.get(key)];
			g.count++;
			if (elementId && !g.elementIds.includes(elementId)) g.elementIds.push(elementId);
		} else {
			seen.set(key, grouped.length);
			grouped.push({
				severity: cr.severity,
				message: cr.message,
				count: 1,
				elementIds: elementId ? [elementId] : [],
			});
		}
	}

	return (
		<div>
			<p className="text-muted small mb-3">{t("boq.validate.gaeb-standard-hint")}</p>
			<div className="list-group list-group-flush">
				{grouped.map((g, i) => {
					// Each error is clickable when it carries an elementId and a click handler.
					// Clicking jumps to that row in the grid AND opens it in edit mode.
					const isClickable = g.elementIds.length > 0 && !!onElementClick;
					return (
						<div
							key={i}
							className={`list-group-item d-flex align-items-center gap-3 px-0 py-2${isClickable ? " boq-validation-item--clickable" : ""
								}`}
							role={isClickable ? "button" : undefined}
							tabIndex={isClickable ? 0 : undefined}
							onClick={isClickable ? () => onElementClick(g.elementIds[0]) : undefined}
							onKeyDown={
								isClickable
									? (e) => {
										if (e.key === "Enter" || e.key === " ") {
											e.preventDefault();
											onElementClick(g.elementIds[0]);
										}
									}
									: undefined
							}
						>
							<SeverityIcon severity={g.severity} />
							<span
								className={`flex-grow-1 small${isClickable ? " text-primary" : ""}`}
							>
								{g.message}
							</span>
							<span className="badge rounded-pill text-primary bg-primary bg-opacity-10 boq-count-badge">
								{g.count}
							</span>
						</div>
					);
				})}
			</div>
		</div>
	);
};

/**
 * BoQ Editor – structure: ProjectHeader, FilterBar, toolbar, content (grid or empty state).
 * Route: /projects/:projectId/lv/:lvId/edit
 */
const BoQEditorPage = () => {
	const { t } = useTranslation();
	const { projectId, lvId } = useParams();
	const navigate = useNavigate();
	const dispatch = useDispatch();

	const projectDetails = useSelector(selectProjectDetails);
	const lv = useSelector(selectBoQLv);
	// Default VAT percentage sourced from the LV's ticket category (falls back to 19%).
	const defaultVat = lv?.ticketCategory?.vat ?? DEFAULT_VAT_PERCENTAGE;
	const loading = useSelector(selectBoQLoading);
	const [saveBoQ, { isLoading: saving }] = useSaveBoQMutation();
	const [reopenBoQ] = useReopenBoQMutation();
	const [validateBoQ] = useValidateBoQMutation();
	const hasUnsavedChanges = useSelector(selectBoQHasUnsavedChanges);
	const gaebData = useSelector(selectBoQGaebData);
	const boqStatus = useSelector(selectBoQStatus);
	const hasInvoice = useSelector(selectBoQHasInvoice);
	const validationResult = useSelector(selectBoQValidationResult);

	// Modal state

	/** 'idle' | 'validating' | 'valid' | 'invalid' */
	const [vfPhase, setVfPhase] = useState("idle");
	const [vfResult, setVfResult] = useState(null);
	const [showVFModal, setShowVFModal] = useState(false);
	const [showSchemaModal, setShowSchemaModal] = useState(false);
	const [runTour, setRunTour] = useState(false);
	const [tourKey, setTourKey] = useState(0);

	const startTour = useCallback(() => {
		setTourKey((k) => k + 1);
		setRunTour(true);
	}, []);

	// OZ item-number schema - initialised from gaebData, user-editable via BoQSchemaModal.
	const [schema, setSchema] = useState(null);
	// Stable ref so callbacks that close over [] don't need schema in their dep array.
	const schemaRef = useRef(null);
	useEffect(() => { schemaRef.current = schema; }, [schema]);

	// Auto-start tour on first visit - mark as seen immediately so a Joyride
	// error or abrupt navigation never leaves the key unset and causes a repeat.
	useEffect(() => {
		if (localStorage.getItem(TOUR_STORAGE_KEY)) return;
		localStorage.setItem(TOUR_STORAGE_KEY, "true");
		const t = setTimeout(() => startTour(), 600);
		return () => clearTimeout(t);
	}, [startTour]);

	const lvName = lv?.name ?? "";

	// Tree: array of root nodes; each node has optional children[]
	const [rows, setRows] = useState([]);
	const [selectedRowId, setSelectedRowId] = useState(null);
	const [selectedRow, setSelectedRow] = useState(null);
	const [editingRowId, setEditingRowId] = useState(null);
	const [expanded, setExpanded] = useState({});
	const [showDeleteRowModal, setShowDeleteRowModal] = useState(false);
	// Copy/cut clipboard: { node, isCut, sourceId } (node = cloned subtree with new ids)
	const [clipboard, setClipboard] = useState({ node: null, isCut: false, sourceId: null });

	// OZ validation errors - re-evaluated after every rows change.
	// Each entry: { rowId, message, errorType }
	const [ozErrors, setOzErrors] = useState([]);
	const ozErrorsRef = useRef([]);
	ozErrorsRef.current = ozErrors;
	const ozErrorRowIds = useMemo(
		() => new Set(ozErrors.map((e) => e.rowId)),
		[ozErrors],
	);

	// Subgroup is available whenever a structural row is selected (GROUP, SUBGROUP, POSITION, or NOTE).
	// Smart placement in handleAddElement handles each case appropriately.
	const canAddSubgroup =
		selectedRow?.type === ELEMENT_TYPES.GROUP ||
		selectedRow?.type === ELEMENT_TYPES.SUBGROUP ||
		selectedRow?.type === ELEMENT_TYPES.POSITION ||
		selectedRow?.type === ELEMENT_TYPES.NOTE;

	// Paste is only valid when the clipboard type fits the current selection's level:
	//   group     → paste at root: selection must be a group (sibling) or nothing (append)
	//   subgroup  → paste inside a group: selection must be a group or subgroup
	//   position/note types → paste inside group or subgroup (or after same-level sibling)
	const canPaste = useMemo(() => {
		if (!clipboard.node) return false;
		const clipType = clipboard.node.type;
		const selType = selectedRow?.type ?? null;
		if (clipType === ELEMENT_TYPES.GROUP) {
			return selType === ELEMENT_TYPES.GROUP || selType === null;
		}
		if (clipType === ELEMENT_TYPES.SUBGROUP) {
			return selType === ELEMENT_TYPES.GROUP || selType === ELEMENT_TYPES.SUBGROUP;
		}
		// position / note family: any node inside a group or subgroup is a valid target
		return (
			selType === ELEMENT_TYPES.GROUP ||
			selType === ELEMENT_TYPES.SUBGROUP ||
			selType === ELEMENT_TYPES.POSITION ||
			selType === ELEMENT_TYPES.NOTE ||
			selType === ELEMENT_TYPES.ADDITIONAL_TEXT ||
			selType === ELEMENT_TYPES.EXECUTION_DESCRIPTION
		);
	}, [clipboard.node, selectedRow]);

	// Track whether rows have been initialized from gaebData (initial load only).
	// We do NOT re-initialize on every save so the editor state isn't discarded.
	const rowsInitialized = useRef(false);

	// Set to true whenever the user makes a structural change (add/edit/delete/move/paste).
	// The auto-save effect watches this so it never fires on the initial load or
	// after a save re-renders the grid with AVA-generated OZ numbers.
	const rowsChangedByUser = useRef(false);

	// Auto-save debounce timer handle.
	const autoSaveTimer = useRef(null);

	// Sequence number guarding against stale save responses overwriting in-flight edits.
	// Incremented on every user change; snapshotted when a save starts so we can detect
	// whether rows mutated while the save was awaiting AVA Cloud.
	const saveSeqRef = useRef(0);

	/**
	 * Apply a structural mutation (add / remove / move / paste) and immediately
	 * regenerate all OZ numbers. Use this instead of raw setRows for any operation
	 * that changes the tree structure.
	 */
	const setRowsStructural = useCallback((mutateFn) => {
		setRows((prev) => {
			const mutated =
				typeof mutateFn === "function" ? mutateFn(prev) : mutateFn;
			return generateItemNumbers(mutated, { schema: schemaRef.current });
		});
	}, []); // schemaRef is a stable ref - no dep needed

	// Re-validate OZ numbers whenever rows change.
	useEffect(() => {
		setOzErrors(validateItemNumbers(rows, schemaRef.current));
	}, [rows]);

	useEffect(() => {
		if (lvId) {
			// Clear any stale rows/state from a previous LV immediately so the
			// loading screen is shown instead of old entries while the fetch runs.
			rowsInitialized.current = false;
			rowsChangedByUser.current = false;
			setRows([]);
			setSelectedRowId(null);
			setSelectedRow(null);
			setEditingRowId(null);
			dispatch(fetchBoQEditorData(lvId));
		}
		// On unmount (or before lvId changes) wipe the Redux slice so no stale
		// gaebData can accidentally seed the next editor session.
		return () => {
			dispatch(clearBoQState());
		};
	}, [lvId, dispatch]);

	// Populate editor rows once when gaebData first arrives from the backend.
	// Apply generateItemNumbers so any OZ from the backend is normalised to our format.
	useEffect(() => {
		if (gaebData && !rowsInitialized.current) {
			rowsInitialized.current = true;
			// Trust item numbers from the backend - do NOT call generateItemNumbers here.
			// generateItemNumbers renumbers from 01, which would overwrite user-set numbers.
			setRows(avaToEditorNodes(gaebData));
			// Sync schema state from the loaded BoQ (or fall back to GAEB default).
			const loadedSchema =
				gaebData?.projectInformation?.itemNumberSchema ?? DEFAULT_GAEB_ITEM_NUMBER_SCHEMA;
			setSchema(loadedSchema);
			schemaRef.current = loadedSchema;
		}
	}, [gaebData]);



	const FILTER_ITEMS = [
		{
			type: "backButton",
			key: "back",
			label: t("common.back"),
		},
	];

	const onFilterAction = (index, key) => {
		if (key === "back") {
			navigate(`/projects/${projectId}/lv`);
		}
	};

	const handleDiscard = () => {
		// Reset editor rows to the last-saved gaebData (undo all unsaved changes)
		if (gaebData) {
			rowsInitialized.current = false;
			setRows(avaToEditorNodes(gaebData));
			rowsInitialized.current = true;
			// Also revert schema to the last-saved value.
			const savedSchema =
				gaebData?.projectInformation?.itemNumberSchema ?? DEFAULT_GAEB_ITEM_NUMBER_SCHEMA;
			setSchema(savedSchema);
			schemaRef.current = savedSchema;
		}
		setSelectedRowId(null);
		setSelectedRow(null);
		setEditingRowId(null);
		dispatch(setHasUnsavedChanges(false));
	};

	/**
	 * Shared save logic. finalize=false → draft; finalize=true → writes lv_positions.
	 * silent=true suppresses the success toast (used by auto-save).
	 */
	const executeSave = useCallback(async (finalize, { silent = false } = {}) => {
		if (!lvId) return;
		if (ozErrorsRef.current.length > 0) {
			if (!silent) showToast(t("boq.save-blocked-oz-errors"), { type: "error" });
			return;
		}

		const updatedAva = editorNodesToAva(rows, gaebData, schemaRef.current);
		// Snapshot the sequence at save start. If the user edits anything during the
		// AVA Cloud roundtrip, saveSeqRef will move past this value and we must NOT
		// adopt the server-recalculated rows (they would clobber the in-flight edits).
		const seqAtStart = saveSeqRef.current;
		try {
			const payload = await saveBoQ({
				projectId,
				lvId,
				gaebData: updatedAva,
				finalize,
			}).unwrap();

			dispatch(setSaveResult(payload));

			const isStillCurrent = saveSeqRef.current === seqAtStart;
			if (!silent && (isStillCurrent || finalize)) {
				showToast(
					t(finalize ? "boq.finalized-success" : "boq.saved-success"),
					{ type: "success" },
				);
			}
			const recalculated = payload?.gaebData;
			if (recalculated && isStillCurrent) {
				// Use item numbers as returned by AVA - it preserved our OZ because we sent them.
				// generateItemNumbers must NOT run here; it would renumber from 01.
				rowsChangedByUser.current = false;
				rowsInitialized.current = false;
				setRows(avaToEditorNodes(recalculated));
				rowsInitialized.current = true;
			}
			// If !isStillCurrent: the user edited during the save, so the response is
			// stale. Leave rows and rowsChangedByUser alone - the auto-save effect will
			// fire again with the latest rows and reconcile on the next round-trip.
		} catch (err) {
			showToast(err?.data?.message || err?.message || t("boq.save-error"), { type: "error" });
		}
	}, [lvId, projectId, rows, gaebData, dispatch, saveBoQ, t]);

	/**
	 * Auto-save: debounced draft save triggered whenever the user commits a value
	 * (i.e. rows state changes). With blur-commit architecture, rows is only
	 * mutated on blur/Enter/structural change, never on raw keystrokes, so the
	 * 1500 ms debounce batches rapid multi-cell edits without risk of firing
	 * mid-word. The immediate flush on editingRowId→null is the primary guardian.
	 * Skipped when: not yet initialized, already saving, or BoQ is finalized.
	 */
	useEffect(() => {
		if (!rowsChangedByUser.current) return;
		if (saving || boqStatus === BOQ_STATUS.FINALIZED) return;

		clearTimeout(autoSaveTimer.current);
		autoSaveTimer.current = setTimeout(() => {
			executeSave(false, { silent: true });
		}, 800);

		return () => clearTimeout(autoSaveTimer.current);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [rows]);

	/**
	 * When the user finishes editing a cell (editingRowId → null), fire any
	 * pending auto-save immediately so OZ numbers appear without further delay.
	 */
	useEffect(() => {
		if (editingRowId !== null) return; // still editing
		if (!rowsChangedByUser.current) return;
		if (saving || boqStatus === BOQ_STATUS.FINALIZED) return;

		clearTimeout(autoSaveTimer.current);
		executeSave(false, { silent: true });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [editingRowId]);

	/** Warn the browser before tab/window close when there are unsaved changes. */
	useEffect(() => {
		if (!hasUnsavedChanges) return;
		const handler = (e) => { e.preventDefault(); };
		window.addEventListener("beforeunload", handler);
		return () => window.removeEventListener("beforeunload", handler);
	}, [hasUnsavedChanges]);

	/** Save as Draft - just recalculate + persist gaebData, no position sync. */
	const handleSaveDraft = useCallback(async () => {
		if (!hasUnsavedChanges) return;
		await executeSave(false);
	}, [hasUnsavedChanges, executeSave]);

	/** Reopen a finalized BoQ - only available when no invoice references its positions. */
	const handleReopenBoQ = useCallback(async () => {
		if (!lvId) return;
		try {
			const payload = await reopenBoQ({ projectId, lvId }).unwrap();
			dispatch(setReopenResult(payload));
			showToast(t("boq.reopen-success"), { type: "success" });
		} catch (err) {
			showToast(err?.data?.message || err?.message || t("boq.reopen-error"), { type: "error" });
		}
	}, [lvId, projectId, dispatch, reopenBoQ, t]);

	/** Finalize - finalizes without re-validation (already checked in V&F flow). */
	const handleFinalizeConfirm = useCallback(async () => {
		setShowVFModal(false);
		setVfPhase("idle");
		setVfResult(null);
		await executeSave(true);
	}, [executeSave]);

	/**
	 * "Validate & Finalize" button handler.
	 * 1. Validates the current editor state (fresh call to AVA Cloud).
	 * 2. Shows result in a modal:
	 *    - If invalid: lists errors, no finalize option.
	 *    - If valid: success message + Finalize confirmation button.
	 */
	const handleValidateAndFinalize = useCallback(async () => {
		if (!lvId) return;
		if (ozErrors.length > 0) {
			showToast(t("boq.save-blocked-oz-errors"), { type: "error" });
			return;
		}
		const updatedAva = editorNodesToAva(rows, gaebData, schemaRef.current);
		setVfPhase("validating");
		setVfResult(null);
		setShowVFModal(true);
		try {
			const result = await validateBoQ({
				projectId,
				lvId,
				gaebData: updatedAva,
			}).unwrap();
			const vr = result.validationResult;
			dispatch(setValidationResult(vr));
			setVfResult(vr);
			setVfPhase(vr?.isSchemaCompliant ? "valid" : "invalid");
		} catch (err) {
			setShowVFModal(false);
			setVfPhase("idle");
			showToast(t("boq.validate.error"), { type: "error" });
		}
	}, [lvId, projectId, rows, gaebData, dispatch, ozErrors.length, validateBoQ, t]);

	/**
	 * Navigate to a specific element in the grid: expand its ancestors,
	 * select it, close any open validation modals, and scroll it into view.
	 */
	const handleNavigateToElement = useCallback(
		(elementId) => {
			if (!elementId) return;
			// Resolve the node so we can set selectedRow (required for toolbar button states).
			const found = findNodeInTree(rows, elementId);
			// Expand all ancestor groups so the target row is visible in the grid.
			const ancestorIds = findAncestorIds(rows, elementId);
			if (ancestorIds?.length) {
				setExpanded((prev) => {
					const next = { ...prev };
					ancestorIds.forEach((id) => { next[id] = true; });
					return next;
				});
			}
			// Select the row immediately so it highlights while the modal is still closing.
			setSelectedRowId(elementId);
			setSelectedRow(found?.node ?? null);
			// Close modals first, then set edit mode and scroll.

			setShowVFModal(false);
			setVfPhase("idle");
			setVfResult(null);
			setEditingRowId(boqStatus === "finalized" ? null : elementId);
			setTimeout(() => {
				document
					.querySelector(`[data-row-id="${elementId}"]`)
					?.scrollIntoView({ behavior: "smooth", block: "center" });
			}, 50);
		},
		[rows, boqStatus],
	);

	const handleSelectionChange = (rowId, rowData) => {
		setSelectedRowId(rowId ?? null);
		setSelectedRow(rowData ?? null);
		// Never enter edit mode for a finalized BoQ.
		setEditingRowId(boqStatus === "finalized" ? null : (rowId ?? null));
	};

	// Mark a user-initiated rows mutation so the auto-save effect fires.
	const markUserChange = () => {
		saveSeqRef.current += 1;
		rowsChangedByUser.current = true;
		dispatch(setHasUnsavedChanges(true));
		// Clear the validation badge - result is stale after any edit.
		dispatch(clearBoQValidation());
	};

	const handleRowChange = (rowId, updates) => {
		setRows((prev) => {
			if ("itemNumber" in updates) {
				// Cascade OZ prefix change to all descendants (groups / subgroups).
				let next = cascadeItemNumberChange(prev, rowId, updates.itemNumber);
				// Resequence siblings that come after the changed node.
				next = resequenceSiblingsAfter(next, rowId, updates.itemNumber, schemaRef.current);
				// Apply any other non-OZ fields in the same update.
				const rest = { ...updates };
				delete rest.itemNumber;
				if (Object.keys(rest).length > 0) {
					next = updateNodeInTree(next, rowId, rest);
				}
				return next;
			}
			return updateNodeInTree(prev, rowId, updates);
		});
		markUserChange();
	};

	/** Force-regenerate all OZ numbers from the current tree structure. */
	const handleResetOZ = useCallback(() => {
		setRows((prev) => generateItemNumbers(prev, { force: true, schema: schemaRef.current }));
		markUserChange();
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	/**
	 * Save the user-edited schema and immediately force-regenerate all OZ numbers.
	 */
	const handleSaveSchema = useCallback((newSchema) => {
		setSchema(newSchema);
		schemaRef.current = newSchema;
		setRows((prev) => generateItemNumbers(prev, { force: true, schema: newSchema }));
		markUserChange();
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	const handleDeleteClick = () => {
		if (!selectedRowId) return;
		setShowDeleteRowModal(true);
	};

	const handleDeleteConfirm = () => {
		if (!selectedRowId) return;
		setRowsStructural((prev) => removeNodeFromTree(prev, selectedRowId));
		setSelectedRowId(null);
		setSelectedRow(null);
		setEditingRowId(null);
		setShowDeleteRowModal(false);
		markUserChange();
	};

	const handleCopy = () => {
		if (!selectedRowId) return;
		const found = findNodeInTree(rows, selectedRowId);
		if (!found?.node) return;
		const cloned = cloneNodeWithNewIds(found.node);
		setClipboard({ node: cloned, isCut: false, sourceId: null });
	};

	const handleCut = () => {
		if (!selectedRowId) return;
		const found = findNodeInTree(rows, selectedRowId);
		if (!found?.node) return;
		const cloned = cloneNodeWithNewIds(found.node);
		setClipboard({ node: cloned, isCut: true, sourceId: selectedRowId });
	};

	const handlePaste = () => {
		const pasted = clipboard.node;
		if (!pasted) return;

		const pastedClone = cloneNodeWithNewIds(pasted);
		// OZ numbers will be regenerated from scratch by setRowsStructural after insertion.

		const clipType = pastedClone.type;

		// When cutting: if the current selection is the source node or one of its
		// descendants, act as if nothing is selected to avoid inserting relative
		// to a node that is about to be removed.
		const selectionIsInsideCutSource =
			clipboard.isCut &&
			clipboard.sourceId &&
			selectedRowId &&
			(selectedRowId === clipboard.sourceId ||
				isDescendantOf(rows, selectedRowId, clipboard.sourceId));

		const effectiveSelType = selectionIsInsideCutSource ? null : (selectedRow?.type ?? null);
		const effectiveSelId = selectionIsInsideCutSource ? null : selectedRowId;

		// Wrap the tree mutation + optional cut-source removal in one setRowsStructural call.
		const doInsert = (insertFn) => {
			setRowsStructural((prev) => {
				let next = insertFn(prev);
				if (clipboard.isCut && clipboard.sourceId) {
					next = removeNodeFromTree(next, clipboard.sourceId);
				}
				return next;
			});
		};

		if (clipType === ELEMENT_TYPES.GROUP) {
			// Groups live at root level.
			if (effectiveSelType === ELEMENT_TYPES.GROUP) {
				doInsert((prev) => addNodeAfterSibling(prev, effectiveSelId, pastedClone));
			} else {
				doInsert((prev) => addNodeToTree(prev, null, pastedClone, "end"));
			}
		} else if (clipType === ELEMENT_TYPES.SUBGROUP) {
			// Subgroups live inside a group.
			if (effectiveSelType === ELEMENT_TYPES.SUBGROUP) {
				// Paste after the selected subgroup (sibling inside the same group).
				doInsert((prev) => addNodeAfterSibling(prev, effectiveSelId, pastedClone));
			} else if (effectiveSelType === ELEMENT_TYPES.GROUP) {
				// Paste as last child of the selected group.
				doInsert((prev) => addNodeToTree(prev, effectiveSelId, pastedClone));
				setExpanded((prev) => ({ ...prev, [effectiveSelId]: true }));
			}
			// No other valid target - canPaste guards the button, but do nothing if called anyway.
		} else {
			// Position, note, additional_text, execution_description.
			if (
				effectiveSelType === ELEMENT_TYPES.POSITION ||
				effectiveSelType === ELEMENT_TYPES.NOTE ||
				effectiveSelType === ELEMENT_TYPES.ADDITIONAL_TEXT ||
				effectiveSelType === ELEMENT_TYPES.EXECUTION_DESCRIPTION
			) {
				// Paste as sibling after the selected item.
				doInsert((prev) => addNodeAfterSibling(prev, effectiveSelId, pastedClone));
			} else if (
				effectiveSelType === ELEMENT_TYPES.GROUP ||
				effectiveSelType === ELEMENT_TYPES.SUBGROUP
			) {
				// Paste as last child of the selected container.
				doInsert((prev) => addNodeToTree(prev, effectiveSelId, pastedClone));
				setExpanded((prev) => ({ ...prev, [effectiveSelId]: true }));
			}
		}

		if (clipboard.isCut) setClipboard({ node: null, isCut: false, sourceId: null });
		setSelectedRowId(pastedClone.id);
		setSelectedRow(pastedClone);
		setEditingRowId(boqStatus === "finalized" ? null : pastedClone.id);
		setExpanded((prev) => ({ ...prev, [pastedClone.id]: true }));
		markUserChange();
	};

	/** Returns true if dropping dragId onto overId with given placement is allowed. */
	const canDropRow = useCallback(
		(dragId, overId, placement = "after") => {
			if (!dragId || !overId || dragId === overId) return false;
			const dragFound = findNodeInTree(rows, dragId);
			const overFound = findNodeInTree(rows, overId);
			if (!dragFound?.node || !overFound?.node) return false;

			const dragNode = dragFound.node;
			const overNode = overFound.node;
			const overParent = overFound.parent;

			if (placement === "inside") {
				// Groups can only live at root - cannot nest inside anything
				if (dragNode.type === ELEMENT_TYPES.GROUP) return false;
				// Subgroups can only be direct children of a group
				if (dragNode.type === ELEMENT_TYPES.SUBGROUP) {
					return overNode.type === ELEMENT_TYPES.GROUP;
				}
				// Positions / notes / etc. can drop inside a group or subgroup
				return overNode.type === ELEMENT_TYPES.GROUP || overNode.type === ELEMENT_TYPES.SUBGROUP;
			}

			// Sibling placement (before / after) - same-level nesting rules
			if (dragNode.type === ELEMENT_TYPES.GROUP) {
				if (overParent !== null) return false;
			}
			if (dragNode.type === ELEMENT_TYPES.SUBGROUP) {
				if (!overParent || overParent.type !== ELEMENT_TYPES.GROUP) return false;
			}
			return true;
		},
		[rows],
	);

	const handleMoveRow = (dragId, overId, placement) => {
		if (!canDropRow(dragId, overId, placement)) return;
		setRowsStructural((prev) => moveNodeInTree(prev, dragId, overId, placement));
		if (placement === "inside") {
			setExpanded((prev) => ({ ...prev, [overId]: true }));
		}
		markUserChange();
	};

	const handleAddElement = (type, position) => {
		const id = uuidv4();
		// Pre-fill taxRate for POSITION rows. defaultVat is a percentage (e.g. 19);
		// taxRate is stored as a decimal (e.g. 0.19) to match the AVA Cloud field.
		const defaultTaxRate =
			type === ELEMENT_TYPES.POSITION && defaultVat != null ? defaultVat / 100 : null;

		const selectNew = (newRow) => {
			setSelectedRowId(id);
			setSelectedRow(newRow);
			setEditingRowId(boqStatus === "finalized" ? null : id);
			// Mark UI as having unsaved changes so the toolbar indicator shows,
			// but do NOT set rowsChangedByUser - that would arm the auto-save
			// timer immediately for an empty/default row. The auto-save fires
			// once the user actually edits content (via handleRowChange).
			dispatch(setHasUnsavedChanges(true));
		};

		// Additional text only at beginning or end of root (position = 'start' | 'end'); no OZ number
		if (type === ELEMENT_TYPES.ADDITIONAL_TEXT && (position === "start" || position === "end")) {
			const newRow = getDefaultBoQRow({
				id,
				type: ELEMENT_TYPES.ADDITIONAL_TEXT,
				itemNumber: null,
				taxRate: defaultTaxRate,
			});
			setRowsStructural((prev) => addNodeToTree(prev, null, newRow, position));
			selectNew(newRow);
			return;
		}

		const selectedType = selectedRow?.type;

		// --- Nesting rules ---
		// Group: always at root level. If a group is selected, add after it (sibling).
		if (type === ELEMENT_TYPES.GROUP) {
			const newRow = getDefaultBoQRow({ id, type, itemNumber: null, taxRate: defaultTaxRate });
			if (selectedType === ELEMENT_TYPES.GROUP) {
				setRowsStructural((prev) => addNodeAfterSibling(prev, selectedRowId, newRow));
			} else {
				setRowsStructural((prev) => addNodeToTree(prev, null, newRow, "end"));
			}
			selectNew(newRow);
			return;
		}

		// Subgroup: smart placement depending on the selected row type.
		if (type === ELEMENT_TYPES.SUBGROUP) {
			// SUBGROUP selected → add sibling after it
			if (selectedType === ELEMENT_TYPES.SUBGROUP) {
				const newRow = getDefaultBoQRow({ id, type, itemNumber: null, taxRate: defaultTaxRate });
				setRowsStructural((prev) => addNodeAfterSibling(prev, selectedRowId, newRow));
				selectNew(newRow);
				return;
			}
			// GROUP selected → add as last child of the selected group
			if (selectedType === ELEMENT_TYPES.GROUP) {
				const newRow = getDefaultBoQRow({ id, type, itemNumber: null, taxRate: defaultTaxRate });
				setRowsStructural((prev) => {
					const found = findNodeInTree(prev, selectedRowId);
					let parentNode = found?.node;
					if (!parentNode || parentNode.type !== ELEMENT_TYPES.GROUP) {
						if (prev.length > 0) {
							const last = prev[prev.length - 1];
							if (last.type === ELEMENT_TYPES.GROUP) parentNode = last;
						}
					}
					if (!parentNode) return prev;
					const existingChildren = Array.isArray(parentNode.children) ? parentNode.children : [];
					return updateNodeInTree(prev, parentNode.id, {
						children: [...existingChildren, { ...newRow, children: [] }],
					});
				});
				setExpanded((prev) => ({ ...prev, [selectedRowId]: true }));
				selectNew(newRow);
				return;
			}
			// POSITION or NOTE selected → walk up to the enclosing GROUP and add subgroup there as last child
			if (selectedType === ELEMENT_TYPES.POSITION || selectedType === ELEMENT_TYPES.NOTE) {
				const posFound = findNodeInTree(rows, selectedRowId);
				const immediateParent = posFound?.parent;
				if (!immediateParent) return;
				// Resolve the enclosing GROUP (immediate parent is GROUP or SUBGROUP→GROUP)
				let groupNode = null;
				if (immediateParent.type === ELEMENT_TYPES.GROUP) {
					groupNode = immediateParent;
				} else if (immediateParent.type === ELEMENT_TYPES.SUBGROUP) {
					const sgFound = findNodeInTree(rows, immediateParent.id);
					if (sgFound?.parent?.type === ELEMENT_TYPES.GROUP) groupNode = sgFound.parent;
				}
				if (!groupNode) return;
				const groupId = groupNode.id;
				const newRow = getDefaultBoQRow({ id, type, itemNumber: null, taxRate: defaultTaxRate });
				setRowsStructural((prev) => {
					const gFound = findNodeInTree(prev, groupId);
					const g = gFound?.node;
					if (!g) return prev;
					const existingChildren = Array.isArray(g.children) ? g.children : [];
					return updateNodeInTree(prev, groupId, {
						children: [...existingChildren, { ...newRow, children: [] }],
					});
				});
				setExpanded((prev) => ({ ...prev, [groupId]: true }));
				selectNew(newRow);
				return;
			}
			return; // No valid selection to derive a group from
		}

		// Position, note, execution_description: add as child of selected group/subgroup, or after same-type sibling.

		if (selectedType === type) {
			// Same type selected → add as sibling (after selected)
			const newRow = getDefaultBoQRow({ id, type, itemNumber: null, taxRate: defaultTaxRate });
			setRowsStructural((prev) => addNodeAfterSibling(prev, selectedRowId, newRow));
			selectNew(newRow);
			return;
		}

		// Add as child of selected group/subgroup
		const canAddAsChild =
			selectedRow &&
			(selectedType === ELEMENT_TYPES.GROUP || selectedType === ELEMENT_TYPES.SUBGROUP);

		if (canAddAsChild) {
			const newRow = getDefaultBoQRow({ id, type, itemNumber: null, taxRate: defaultTaxRate });
			setRowsStructural((prev) => addNodeToTree(prev, selectedRowId, newRow));
			setExpanded((prev) => ({ ...prev, [selectedRowId]: true }));
			selectNew(newRow);
		} else {
			// No valid parent selected → add at root
			const newRow = getDefaultBoQRow({ id, type, itemNumber: null, taxRate: defaultTaxRate });
			setRowsStructural((prev) => addNodeToTree(prev, null, newRow, "end"));
			selectNew(newRow);
		}
	};

	const handleContextMenuAction = (rowId, elementType, placement) => {
		const id = uuidv4();
		const defaultTaxRate =
			elementType === ELEMENT_TYPES.POSITION && defaultVat != null ? defaultVat / 100 : null;
		const newRow = getDefaultBoQRow({ id, type: elementType, itemNumber: null, taxRate: defaultTaxRate });
		if (placement === "inside") {
			setRowsStructural((prev) => addNodeToTree(prev, rowId, newRow, "end"));
			setExpanded((prev) => ({ ...prev, [rowId]: true }));
		} else {
			setRowsStructural((prev) => addNodeAfterSibling(prev, rowId, newRow));
		}
		setSelectedRowId(id);
		setSelectedRow(newRow);
		setEditingRowId(boqStatus === "finalized" ? null : id);
		dispatch(setHasUnsavedChanges(true));
		markUserChange();
	};

	return (
		<div className="boq-editor-page">
			<ProjectHeader
				projectName={projectDetails?.name ?? ""}
				tabTitle={`${t("boq.title")} | ${lvName}`}
			/>
			<div className="page-container">
				<FilterBar
					items={FILTER_ITEMS}
					onFilterAction={onFilterAction}
				/>

				<BoQToolbar
					onAddElement={handleAddElement}
					canAddSubgroup={canAddSubgroup}
					onCopy={handleCopy}
					onCut={handleCut}
					onPaste={handlePaste}
					onDelete={handleDeleteClick}
					onDefineSchema={() => setShowSchemaModal(true)}
					onStartTour={startTour}
					onDiscard={handleDiscard}
					onSaveDraft={handleSaveDraft}
					onValidateAndFinalize={handleValidateAndFinalize}
					onReopenBoQ={handleReopenBoQ}
					hasInvoice={hasInvoice}
					hasUnsavedChanges={hasUnsavedChanges}
					hasOzErrors={ozErrors.length > 0}
					isSaving={saving}
					isVFValidating={vfPhase === "validating"}
					validationResult={validationResult}
					onValidationElementClick={handleNavigateToElement}
					boqStatus={boqStatus}
					canDelete={!!selectedRowId}
					canCopy={!!selectedRowId}
					canPaste={canPaste}
				/>

				<div className="boq-oz-hint">
					{ozErrors.length > 0 ? (
						<small className="d-flex align-items-center gap-2 text-danger">
							<i>{t("boq.oz-errors", { count: ozErrors.length })}</i>
							<button
								type="button"
								className="btn btn-link btn-sm p-0 text-danger text-decoration-underline"
								onClick={handleResetOZ}
							>
								{t("boq.oz-reset")}
							</button>
						</small>
					) : (
						<small className="d-flex align-items-center gap-2 text-muted">
							<i>{t("boq.oz-hint")}</i>
							{boqStatus !== BOQ_STATUS.FINALIZED && (
								<button
									type="button"
									className="btn btn-link btn-sm p-0 text-muted"
									onClick={handleResetOZ}
								>
									{t("boq.oz-reset")}
								</button>
							)}
						</small>
					)}
				</div>

				<ModalAtom
					show={showDeleteRowModal}
					handleClose={() => setShowDeleteRowModal(false)}
					handleSubmit={handleDeleteConfirm}
					title={t("boq.delete-row-title")}
					body={<p>{t("boq.delete-row-confirm")}</p>}
					saveText="delete"
				/>


				{/* Validate & Finalize modal - validates fresh, then confirms */}
				<ModalAtom
					show={showVFModal}
					handleClose={() => { setShowVFModal(false); setVfPhase("idle"); setVfResult(null); }}
					handleSubmit={vfPhase === "valid" ? handleFinalizeConfirm : () => { setShowVFModal(false); setVfPhase("idle"); setVfResult(null); }}
					title={t("boq.validate.and-finalize-title")}
					body={
						<div>
							{vfPhase === "validating" ? (
								<div className="d-flex align-items-center gap-2 py-2">
									<Spinner animation="border" size="sm" />
									<span>{t("boq.validate.validating")}</span>
								</div>
							) : (
								<div>
									<ValidationResultBody result={vfResult} t={t} onElementClick={handleNavigateToElement} />
									{vfPhase === "valid" && (
										<p className="mt-3 mb-0">{t("boq.finalize-confirm")}</p>
									)}
								</div>
							)}
						</div>
					}
					saveText={vfPhase === "valid" ? "common.confirm" : "common.close"}
					hideSaveButton={vfPhase === "validating"}
					hideCloseButton={vfPhase !== "valid"}
				/>

				{/* Schema editor modal - "Struktur festlegen" */}
				<BoQSchemaModal
					show={showSchemaModal}
					onHide={() => setShowSchemaModal(false)}
					schema={schema ?? DEFAULT_GAEB_ITEM_NUMBER_SCHEMA}
					onSave={handleSaveSchema}
				/>

				{/* Guided walkthrough */}
				<BoQEditorTour key={tourKey} run={runTour} onFinish={() => setRunTour(false)} />

				<div className="boq-editor-content">
					{loading ? (
						<div className="boq-editor-loading">
							<Spinner animation="border" size="sm" />
							<span className="ms-2">{t("common.loading")}</span>
						</div>
					) : (
						<>
							{countRootNodes(rows) === 0 ? (
								<BoQEmptyState
									onAddElement={handleAddElement}
									canAddSubgroup={canAddSubgroup}
								/>
							) : (
								<>
									<BoQGrid
										data={rows}
										onRowChange={handleRowChange}
										onSelectionChange={handleSelectionChange}
										selectedRowId={selectedRowId}
										editingRowId={editingRowId}
										expanded={expanded}
										onExpandedChange={setExpanded}
										onMoveRow={boqStatus === "finalized" ? undefined : handleMoveRow}
										onCanDrop={canDropRow}
										readOnly={boqStatus === "finalized"}
										onContextMenuAction={boqStatus === "finalized" ? undefined : handleContextMenuAction}
										ozErrorRowIds={ozErrorRowIds}
									/>
									{/* BoQLegend removed – element type is now shown as badge on each row */}
								</>
							)}
						</>
					)}
				</div>
			</div>
		</div>
	);
};

export default BoQEditorPage;
